export type PGSClientConfig = {
  tenantToken: string;
  paymentToken?: string;
  paymentMethod?: string;
  pgsUrl: string;
  authInitUrl: string;
  merchant?: string;
  merchantId?: string;
  successUrl: string;
  cancelUrl?: string;
  errorUrl?: string;
  buttonName?: string;
  buttonClassName?: string;
  ciamToken?: string;
};

export type PaymentSession = {
  session_id: string;
  merchant_id: string;
};

type Session = {
  data: {
    uuid: string;
    session: string;
  };
};

type Options = {
  subscription_uuid: string;
};

export type SecuredFieldsCreditCardDetails = {
  cardBrand: string;
  maskedCardNumber: string;
};

export type SecuredFieldsFieldErrorType = "invalid" | "missing";

export type SecuredFieldsValidationResult =
  | { status: "ok"; sessionId: string; cardDetails?: SecuredFieldsCreditCardDetails }
  | { status: "fields_in_error"; errors: Record<string, SecuredFieldsFieldErrorType> }
  | { status: "error"; message: string };

export type SecuredFieldsConfig = {
  session: string;
  fields: Record<string, string>;
  locale?: string;
  onInitialized?: (status: "ok" | "error", message?: string) => void;
  onFormUpdate?: (result: SecuredFieldsValidationResult) => void;
};

export type SecuredFieldsSessionConfig = {
  merchantId: string;
};

export type SecuredFieldsSessionInstance = {
  loadSdk(): Promise<void>;
  configure(config: SecuredFieldsConfig): void;
  validate(): void;
  destroy(): void;
  setFocusStyle(selectors: string[], styles: Record<string, string>): void;
  setHoverStyle(selectors: string[], styles: Record<string, string>): void;
  setPlaceholderStyle(selectors: string[], styles: Record<string, string>): void;
  onBlur(selectors: string[], callback: (selector: string) => void): void;
  onFocus(selectors: string[], callback: (selector: string) => void): void;
  onChange(selectors: string[], callback: (selector: string) => void): void;
  onValidityChange(
    selectors: string[],
    callback: (selector: string, result: { isValid: boolean; isIncomplete: boolean }) => void
  ): void;
};

export type SecuredFieldsSessionConstructor = {
  new (config: SecuredFieldsSessionConfig): SecuredFieldsSessionInstance;
  prototype: SecuredFieldsSessionInstance;
};

export type PGSClientInstance = {
  config: PGSClientConfig;
  _initCheckout(): Promise<void>;
  _initPaymentSession(): Promise<unknown>;
  _pay(paymentSession: PaymentSession): Promise<void>;
  pay(paymentSession?: PaymentSession): Promise<void>;
  mount(element: HTMLElement): () => void;
  process(): Promise<void>;
  initAuthorization(options: Options): Promise<Session>;
  authorize(session: Session): Promise<void>;
};

export const PaymentFlowType = {
  HOSTED_CHECKOUT: "HOSTED_CHECKOUT",
  SECURED_FIELDS: "SECURED_FIELDS",
} as const;

export type PaymentFlowType = (typeof PaymentFlowType)[keyof typeof PaymentFlowType];

export type PGSClientConstructor = {
  new (config: PGSClientConfig): PGSClientInstance;
  getSupportedPaymentFlows?: (paymentMethodCode: string) => PaymentFlowType[];
};
